import React, { useContext, useEffect, useState } from "react";
import { useAuthContext } from "../../../contexts/AuthContext";

import Sidebar from "../../UI/Sidebar";
import Loader from "../../UI/Loader";

import { sfQuery } from "../../API";
import { useParams } from "react-router";

// Move getMonth utility function to the top level, outside the component, so it is available to all helpers
function getMonth(dt) {
  return dt.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
}

// Currency conversion helper - CAD to USD using 1.39 exchange rate
function convertToUSD(amount, currencyCode) {
  if (!amount || currencyCode === "USD") return amount;
  if (currencyCode === "CAD") return amount / 1.39;
  return amount; // Default to no conversion for unknown currencies
}

function sanitizeDateString(dateStr) {
  if (typeof dateStr !== "string") return dateStr;
  const sanitized = dateStr.replace(/[^\d/-]/g, "");
  if (sanitized !== dateStr) {
    console.warn(`Sanitized date string from "${dateStr}" to "${sanitized}"`);
  }
  return sanitized;
}

function parseLocalDate(dateStr) {
  if (typeof dateStr !== "string") return new Date(dateStr);
  const sanitized = sanitizeDateString(dateStr);
  const [year, month, day] = sanitized.split("-").map(Number);
  if (
    isNaN(year) ||
    isNaN(month) ||
    isNaN(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    console.warn(`Invalid date string: "${dateStr}"`);
    return null;
  }
  return new Date(year, month - 1, day);
}

function procesCancellations(orderProds) {
  const groupedData = {};
  orderProds.forEach((op) => {
    const cancelledDateStr = op.Order.Cancelled_Date__c || op.Cancelled_Date__c;
    const cancelledDate = parseLocalDate(cancelledDateStr);
    if (!cancelledDate) return;
    const month = getMonth(cancelledDate);
    if (!groupedData[month]) {
      groupedData[month] = { Cancellations: [] };
    }
    groupedData[month].Cancellations.push(op);
  });
  return groupedData;
}

function processTransactions(transactions) {
  const groupedData = {};
  transactions.forEach((op) => {
    const transactionDateStr = op.Transaction_Date__c;
    const transactionDate = parseLocalDate(transactionDateStr);
    if (!transactionDate) return;
    const month = getMonth(transactionDate);
    if (!groupedData[month]) {
      groupedData[month] = { Transactions: [] };
    }
    groupedData[month].Transactions.push(op);
  });
  return groupedData;
}

function processGMAndMIOrders(gmOrders, miOrders) {
  // Create a map of MI orders by AccountId for easier lookup
  const miOrdersByAccount = {};
  miOrders.forEach((mi) => {
    if (!miOrdersByAccount[mi.AccountId]) {
      miOrdersByAccount[mi.AccountId] = [];
    }
    miOrdersByAccount[mi.AccountId].push(mi);
  });

  // Process GM orders and match with MI orders
  return gmOrders.map((gmOrder) => {
    const accountMIOrders = miOrdersByAccount[gmOrder.AccountId] || [];

    // Check if this is a corporate billing child account
    const isCorporateBillingChild =
      gmOrder.Account.ParentId && gmOrder.Account.Parent.Corporate_Billing__c;

    // Get current and last month dates
    const currentDate = new Date();
    const lastMonthDate = new Date();
    lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);

    const currentMonth = getMonth(currentDate);
    const lastMonth = getMonth(lastMonthDate);

    // Find MI orders for current and last month
    const currentMonthMI =
      accountMIOrders.find((mi) => {
        const paymentDate = parseLocalDate(mi.Payment_Date_new__c);
        return paymentDate && getMonth(paymentDate) === currentMonth;
      }) || {};

    const lastMonthMI =
      accountMIOrders.find((mi) => {
        const paymentDate = parseLocalDate(mi.Payment_Date_new__c);
        return paymentDate && getMonth(paymentDate) === lastMonth;
      }) || {};

    // For corporate billing child accounts, modify the data to show as paid and $0 amount
    let modifiedGMOrder = { ...gmOrder };
    let modifiedCurrentMonthMI = { ...currentMonthMI };
    let modifiedLastMonthMI = { ...lastMonthMI };

    if (isCorporateBillingChild) {
      // Set amounts to 0 to avoid double counting
      modifiedGMOrder.Total_Amount__c = 0;

      // Mark as paid to show these accounts as never overdue
      if (currentMonthMI.Id) {
        modifiedCurrentMonthMI.Receive_Final_Payment__c =
          new Date().toISOString(); // Mark as paid today
      }
      if (lastMonthMI.Id) {
        modifiedLastMonthMI.Receive_Final_Payment__c = new Date().toISOString(); // Mark as paid today
      }
    }

    console.log(accountMIOrders);
    console.log(lastMonthMI);

    return {
      GMOrder: modifiedGMOrder,
      ThisMonthMIOrder: modifiedCurrentMonthMI,
      LastMonthMIOrder: modifiedLastMonthMI,
      IsOneOff: false, // Can be enhanced based on business logic
      IsProrated: false, // Can be enhanced based on business logic
      IsOverdue: false, // Can be enhanced based on business logic
      IsCorporateBillingChild: isCorporateBillingChild, // Track this for display purposes
      ConvertedAmount: convertToUSD(
        modifiedGMOrder.Total_Amount__c || 0,
        modifiedGMOrder.CurrencyIsoCode
      ), // For calculations
      CurrencyCode: modifiedGMOrder.CurrencyIsoCode || "USD", // Track original currency
    };
  });
}

const CashFlowDashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancellations, setCancellations] = useState({});
  const [refunds, setRefunds] = useState({});
  const [credits, setCredits] = useState({});
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [lastMonthActualCollection, setLastMonthActualCollection] = useState(0);
  const [currentMonthActualCollection, setCurrentMonthActualCollection] =
    useState(0);
  const context = useAuthContext();

  let { owId, sidebar } = useParams();

  // Utility Functions
  const formatCurrency = (value, currencyCode = "USD") => {
    return value !== null && value !== undefined
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currencyCode,
        }).format(value)
      : "";
  };

  // Format currency with original currency shown next to USD totals
  const formatCurrencyWithOriginal = (value, currencyCode = "USD") => {
    if (!value || currencyCode === "USD") return formatCurrency(value);
    const convertedValue = convertToUSD(value, currencyCode);
    return `${formatCurrency(convertedValue)} (${formatCurrency(
      value,
      currencyCode
    )})`;
  };

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.slice(0, maxLength) + "...";
    }
    return text;
  };

  useEffect(() => {
    if (!context?.ready) return;
    setLoading(true);
    console.log("Starting data fetch for CashFlowDashboard...");

    const fetchData = async () => {
      try {
        // Fetch GM Orders
        console.log("Fetching GM orders...");
        const gmorders = await sfQuery(
          "Order",
          "Id, AccountId, Account.Name, Account.Activated_Date__c, Account.ParentId, Account.Parent.Corporate_Billing__c, Payment_Date_new__c, Receive_Final_Payment__c, Next_Billing_Date__c, Total_Amount__c, Total_Discount_Calculated__c, CurrencyIsoCode",
          `Order_Label__c LIKE 'GM-%' AND Status='Activated' AND (NOT Account.Name LIKE 'Test %') AND Account.RecordType.Name LIKE '%Customer%'`,
          context
        );
        console.log("GM Orders fetched:", gmorders?.length || 0);

        // Fetch MI Orders
        console.log("Fetching MI orders...");
        const miorders = await sfQuery(
          "Order",
          "Id, AccountId, Account.Name, Account.Activated_Date__c, Account.ParentId, Account.Parent.Corporate_Billing__c, Order_Label__c, Receive_Final_Payment__c, Next_Billing_Date__c, Total_Amount__c, Total_Discount_Calculated__c, Payment_Date_new__c, CurrencyIsoCode",
          `Order_Label__c LIKE 'MI-%' AND Payment_Date_new__c >= LAST_N_MONTHS:3 AND (NOT Account.Name LIKE 'Test %') AND Account.RecordType.Name LIKE '%Customer%' ORDER BY Payment_Date_new__c DESC`,
          context
        );
        console.log("MI Orders fetched:", miorders?.length || 0);

        // Fetch Cancellations
        console.log("Fetching cancellations...");
        const cancellationsData = await sfQuery(
          "OrderItem",
          "MRR__c, Cancelled_Date__c, Order.Cancelled_Date__c",
          `Order.Order_Label__c LIKE 'GM-%' AND (NOT Product2.ProductCode IN ('HGGSPADSPEND', 'HGPPCADSPEND')) AND (NOT Order.Account.Name LIKE '%test %') AND ((Cancelled_Date__c >= LAST_N_MONTHS:6 AND Is_Cancelled__c = TRUE AND Part_of_Cancelled_Order__c = FALSE AND MRR_Exception__c = false) OR (Order.Status IN ('Cancelled', 'Ala Carte Non Member') AND Order.Cancelled_Date__c >= LAST_N_MONTHS:6 AND Part_of_Cancelled_Order__c = TRUE AND Is_Cancelled__c = TRUE AND MRR_Exception__c = false))`,
          context
        );
        console.log("Cancellations fetched:", cancellationsData?.length || 0);

        // Fetch Refunds
        console.log("Fetching refunds...");
        const refundsData = await sfQuery(
          "Transaction__c",
          "Transaction_Amount__c, Transaction_Date__c",
          `RecordType.Name IN ('Partial Refund', 'Refund / Cancel Order') AND Transaction_Date__c >= LAST_N_MONTHS:6`,
          context
        );
        console.log("Refunds fetched:", refundsData?.length || 0);

        // Fetch Credits
        console.log("Fetching credits...");
        const creditsData = await sfQuery(
          "Transaction__c",
          "Transaction_Amount__c, Transaction_Date__c",
          `RecordType.Name IN ('Credit') AND Transaction_Date__c >= LAST_N_MONTHS:6`,
          context
        );
        console.log("Credits fetched:", creditsData?.length || 0);

        // Process and combine the data
        const processedData = processGMAndMIOrders(gmorders, miorders);

        setData(processedData);
        setCancellations(procesCancellations(cancellationsData || []));
        setRefunds(processTransactions(refundsData || []));
        setCredits(processTransactions(creditsData || []));
        setError(false);
        console.log("Data fetch completed successfully");
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [context]);

  useEffect(() => {
    if (data && cancellations && refunds && credits) {
      // Determine Last Month
      const lastMonthDate = new Date();
      lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
      const lastMonth = getMonth(lastMonthDate);

      // Calculate Last Month Actual Collection
      const lastMonthData = data;
      const lastMonthSearchFiltered = lastMonthData.filter((item) =>
        item.GMOrder.Account.Name.toLowerCase().includes(
          searchQuery.trim().toLowerCase()
        )
      );
      const lastMonthTotalPaid = lastMonthSearchFiltered
        .filter(
          (item) =>
            item.LastMonthMIOrder.Id && // MI order exists for last month
            item.LastMonthMIOrder.Receive_Final_Payment__c && // Payment WAS received
            item.ConvertedAmount !== 0
        )
        .reduce((total, item) => total + (item.ConvertedAmount || 0), 0);

      const lastMonthRefundsTotal = refunds[lastMonth]?.Transactions
        ? refunds[lastMonth].Transactions.reduce(
            (total, txn) => total + (txn.Transaction_Amount__c || 0),
            0
          )
        : 0;

      const lastMonthCreditsTotal = credits[lastMonth]?.Transactions
        ? credits[lastMonth].Transactions.reduce(
            (total, txn) => total + (txn.Transaction_Amount__c || 0),
            0
          )
        : 0;

      const lastMonthCancellationsTotal = cancellations[lastMonth]
        ?.Cancellations
        ? cancellations[lastMonth].Cancellations.reduce(
            (total, cancel) => total + (cancel.MRR__c || 0),
            0
          )
        : 0;

      const lastMonthActual =
        lastMonthTotalPaid -
        lastMonthRefundsTotal -
        lastMonthCreditsTotal -
        lastMonthCancellationsTotal;
      setLastMonthActualCollection(lastMonthActual);

      // Determine Current Month
      const currentMonth = getMonth(new Date());

      // Calculate Current Month Actual Collection
      const currentMonthData = data;
      const currentMonthSearchFiltered = currentMonthData.filter((item) =>
        item.GMOrder.Account.Name.toLowerCase().includes(
          searchQuery.trim().toLowerCase()
        )
      );
      const currentMonthTotalPaid = currentMonthSearchFiltered
        .filter(
          (item) =>
            item.ThisMonthMIOrder.Id && // MI order exists for current month
            item.ThisMonthMIOrder.Receive_Final_Payment__c && // Payment WAS received
            item.ConvertedAmount !== 0
        )
        .reduce((total, item) => total + (item.ConvertedAmount || 0), 0);

      const currentMonthTotalPending = currentMonthSearchFiltered
        .filter(
          (item) =>
            item.ThisMonthMIOrder.Id && // MI order exists for current month
            !item.ThisMonthMIOrder.Receive_Final_Payment__c && // Payment NOT received yet
            item.GMOrder.Next_Billing_Date__c &&
            parseLocalDate(item.GMOrder.Next_Billing_Date__c) > new Date() && // Future billing date (not overdue)
            item.ConvertedAmount !== 0
        )
        .reduce((total, item) => total + (item.ConvertedAmount || 0), 0);

      const currentMonthRefundsTotal = refunds[currentMonth]?.Transactions
        ? refunds[currentMonth].Transactions.reduce(
            (total, txn) => total + (txn.Transaction_Amount__c || 0),
            0
          )
        : 0;

      const currentMonthCreditsTotal = credits[currentMonth]?.Transactions
        ? credits[currentMonth].Transactions.reduce(
            (total, txn) => total + (txn.Transaction_Amount__c || 0),
            0
          )
        : 0;

      const currentMonthCancellationsTotal = cancellations[currentMonth]
        ?.Cancellations
        ? cancellations[currentMonth].Cancellations.reduce(
            (total, cancel) => total + (cancel.MRR__c || 0),
            0
          )
        : 0;

      const currentMonthActual =
        currentMonthTotalPaid +
        currentMonthTotalPending -
        currentMonthRefundsTotal -
        currentMonthCreditsTotal -
        currentMonthCancellationsTotal;
      setCurrentMonthActualCollection(currentMonthActual);
    }
  }, [data, cancellations, refunds, credits, searchQuery]);

  // Determine Color Based on Comparison
  const isCurrentHigher =
    currentMonthActualCollection > lastMonthActualCollection;
  // Using Unicode arrows for visual indicators
  const actualCollectionIndicator = isCurrentHigher ? "▲" : "▼";
  const actualCollectionColor = isCurrentHigher ? "#28a745" : "#dc3545"; // Green or Red

  // Helper function to check if account was a client in previous month
  const wasClientInPreviousMonth = (activatedDate) => {
    if (!activatedDate) return false;

    const activated = parseLocalDate(activatedDate);
    if (!activated) return false;

    // Get the start of previous month
    const lastMonthDate = new Date();
    lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
    const previousMonthStart = new Date(
      lastMonthDate.getFullYear(),
      lastMonthDate.getMonth(),
      1
    );

    // Account was a client in previous month if activated before the start of previous month
    return activated < previousMonthStart;
  };

  // Helper function to check if account is a "new" client (activated in last 60 days)
  const isNewClient = (activatedDate) => {
    if (!activatedDate) return false;

    const activated = parseLocalDate(activatedDate);
    if (!activated) return false;

    // Check if activated within last 60 days
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

    return activated >= sixtyDaysAgo;
  };

  // Notes Generator
  const getNotes = (row) => {
    return [
      row.IsOneOff && (
        <span
          key="one-off"
          style={{ fontSize: "0.8em", color: "#007bff", marginLeft: "5px" }}
        >
          One-Off{" "}
        </span>
      ),
      row.IsProrated && (
        <span
          key="prorated"
          style={{ fontSize: "0.8em", color: "#007bff", marginLeft: "5px" }}
        >
          Prorated (
          {new Date(
            row.Opportunity.Agreement_Start_Date__c
          ).toLocaleDateString()}
          )
        </span>
      ),
      row.IsOverdue && (
        <span
          key="overdue"
          style={{ fontSize: "0.8em", color: "#ff4d4d", marginLeft: "5px" }}
        >
          Overdue
        </span>
      ),
      row.IsCorporateBillingChild && (
        <span
          key="corporate-billing"
          style={{ fontSize: "0.8em", color: "#6f42c1", marginLeft: "5px" }}
        >
          Corporate Billing
        </span>
      ),
      isNewClient(row.GMOrder.Account.Activated_Date__c) && (
        <span
          key="new-client"
          style={{
            fontSize: "0.8em",
            color: "#ffffff",
            backgroundColor: "#28a745",
            padding: "2px 6px",
            borderRadius: "3px",
            marginLeft: "5px",
          }}
        >
          New
        </span>
      ),
    ].filter(Boolean);
  };

  // Render Table Function
  const renderTable = (
    rows,
    isLastMonth,
    refundsData = { Transactions: [] },
    creditsData = { Transactions: [] },
    cancellationsData = { Cancellations: [] },
    actualCollectionColorOverride
  ) => {
    const isSearchActive = searchQuery.trim() !== "";

    const filteredRows = rows.filter((row) => {
      if (isLastMonth) {
        // For last month table: only show accounts that were clients in the previous month
        return wasClientInPreviousMonth(row.GMOrder.Account.Activated_Date__c);
      }
      // For current month table: show all accounts
      return true;
    });

    const searchFilteredRows = filteredRows.filter((row) =>
      row.GMOrder.Account.Name.toLowerCase().includes(
        searchQuery.trim().toLowerCase()
      )
    );

    const totalDue = isLastMonth
      ? searchFilteredRows
          .filter(
            (item) =>
              item.LastMonthMIOrder.Id && // MI order exists for last month
              !item.LastMonthMIOrder.Receive_Final_Payment__c && // Payment NOT received (overdue)
              item.ConvertedAmount !== 0
          )
          .reduce((total, item) => total + (item.ConvertedAmount || 0), 0)
      : searchFilteredRows
          .filter(
            (item) =>
              item.ThisMonthMIOrder.Id && // MI order exists for current month
              !item.ThisMonthMIOrder.Receive_Final_Payment__c && // Payment NOT received (overdue)
              item.ConvertedAmount !== 0 &&
              (!item.GMOrder.Next_Billing_Date__c ||
                parseLocalDate(item.GMOrder.Next_Billing_Date__c) <= new Date()) // Past due or no future date
          )
          .reduce((total, item) => total + (item.ConvertedAmount || 0), 0);

    const totalPending = isLastMonth
      ? 0
      : searchFilteredRows
          .filter(
            (item) =>
              item.ThisMonthMIOrder.Id && // MI order exists for current month
              !item.ThisMonthMIOrder.Receive_Final_Payment__c && // Payment NOT received yet
              item.GMOrder.Next_Billing_Date__c &&
              parseLocalDate(item.GMOrder.Next_Billing_Date__c) > new Date() && // Future billing date (not overdue)
              item.ConvertedAmount !== 0
          )
          .reduce((total, item) => total + (item.ConvertedAmount || 0), 0);

    const totalPaid = searchFilteredRows
      .filter(
        (item) =>
          (isLastMonth
            ? item.LastMonthMIOrder.Receive_Final_Payment__c
            : item.ThisMonthMIOrder.Receive_Final_Payment__c) ||
          item.ConvertedAmount === 0
      )
      .reduce((total, item) => total + (item.ConvertedAmount || 0), 0);

    const totalRefunds = refundsData.Transactions
      ? refundsData.Transactions.reduce(
          (total, txn) => total + (txn.Transaction_Amount__c || 0),
          0
        )
      : 0;

    const totalCredits = creditsData.Transactions
      ? creditsData.Transactions.reduce(
          (total, txn) => total + (txn.Transaction_Amount__c || 0),
          0
        )
      : 0;

    const totalCancellations = cancellationsData.Cancellations
      ? cancellationsData.Cancellations.reduce(
          (total, cancel) => total + (cancel.MRR__c || 0),
          0
        )
      : 0;

    const actualCollection = isLastMonth
      ? totalPaid - totalRefunds - totalCredits - totalCancellations
      : totalPaid +
        totalPending -
        totalRefunds -
        totalCredits -
        totalCancellations;

    return (
      <div style={{ marginBottom: "20px" }}>
        {/* Hide totals when search is active */}
        {!isSearchActive && (
          <>
            <h5>Collected: {formatCurrency(totalPaid)}</h5>
            {!isLastMonth && (
              <>
                <h5>Total Due: {formatCurrency(totalDue)}</h5>
                <h5>Total Pending: {formatCurrency(totalPending)}</h5>
              </>
            )}
            {isLastMonth && <h5>Total Due: {formatCurrency(totalDue)}</h5>}
            <h5 className="negative">
              Refunds: {formatCurrency(totalRefunds)}
            </h5>
            <h5 className="negative">
              Credits: {formatCurrency(totalCredits)}
            </h5>
            <h5 className="negative">
              Cancellations: {formatCurrency(totalCancellations)}
            </h5>
            <h4
              style={{
                display: "flex",
                alignItems: "center",
                color: actualCollectionColorOverride || "#000000",
              }}
            >
              Actual Collection: {formatCurrency(actualCollection)}
              {!isLastMonth && (
                <span style={{ marginLeft: "8px" }}>
                  {isCurrentHigher ? "▲" : "▼"}
                </span>
              )}
            </h4>
          </>
        )}
        <table>
          <thead>
            <tr>
              <th style={{ padding: "4px", fontSize: "11px" }}>Account</th>
              <th style={{ padding: "4px", fontSize: "11px" }}>Paid</th>
              <th style={{ padding: "4px", fontSize: "11px" }}>Amount</th>
              <th style={{ padding: "4px", fontSize: "11px" }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {searchFilteredRows
              .sort((a, b) => {
                if (!isLastMonth) {
                  // Current month: Check for overdue items (MI exists but payment not received and past due date)
                  const isOverdueA =
                    a.ThisMonthMIOrder.Id && // MI order exists
                    !a.ThisMonthMIOrder.Receive_Final_Payment__c && // Payment not received
                    a.ConvertedAmount !== 0 &&
                    (!a.GMOrder.Next_Billing_Date__c ||
                      parseLocalDate(a.GMOrder.Next_Billing_Date__c) <=
                        new Date()); // Past due or no future date

                  const isOverdueB =
                    b.ThisMonthMIOrder.Id && // MI order exists
                    !b.ThisMonthMIOrder.Receive_Final_Payment__c && // Payment not received
                    b.ConvertedAmount !== 0 &&
                    (!b.GMOrder.Next_Billing_Date__c ||
                      parseLocalDate(b.GMOrder.Next_Billing_Date__c) <=
                        new Date()); // Past due or no future date

                  if (isOverdueA && !isOverdueB) return -1;
                  if (!isOverdueA && isOverdueB) return 1;

                  // Then check for scheduled payments (MI exists, payment not received, future billing date)
                  const nextBillingDateA = parseLocalDate(
                    a.GMOrder.Next_Billing_Date__c
                  );
                  const isScheduledA =
                    a.ThisMonthMIOrder.Id &&
                    !a.ThisMonthMIOrder.Receive_Final_Payment__c &&
                    nextBillingDateA &&
                    nextBillingDateA > new Date() &&
                    a.ConvertedAmount !== 0;

                  const nextBillingDateB = parseLocalDate(
                    b.GMOrder.Next_Billing_Date__c
                  );
                  const isScheduledB =
                    b.ThisMonthMIOrder.Id &&
                    !b.ThisMonthMIOrder.Receive_Final_Payment__c &&
                    nextBillingDateB &&
                    nextBillingDateB > new Date() &&
                    b.ConvertedAmount !== 0;

                  if (isScheduledA && !isScheduledB) return -1;
                  if (!isScheduledA && isScheduledB) return 1;
                  if (isScheduledA && isScheduledB) {
                    return nextBillingDateA - nextBillingDateB;
                  }
                }

                if (isLastMonth) {
                  const isOverdueA =
                    a.LastMonthMIOrder.Id && // MI order exists
                    !a.LastMonthMIOrder.Receive_Final_Payment__c &&
                    a.ConvertedAmount !== 0;
                  const isOverdueB =
                    b.LastMonthMIOrder.Id && // MI order exists
                    !b.LastMonthMIOrder.Receive_Final_Payment__c &&
                    b.ConvertedAmount !== 0;

                  if (isOverdueA && !isOverdueB) return -1;
                  if (!isOverdueA && isOverdueB) return 1;
                  if (isOverdueA && isOverdueB) {
                    const nextBillingDateA = parseLocalDate(
                      a.GMOrder.Next_Billing_Date__c
                    );
                    const nextBillingDateB = parseLocalDate(
                      b.GMOrder.Next_Billing_Date__c
                    );

                    if (nextBillingDateA && nextBillingDateB) {
                      return nextBillingDateA - nextBillingDateB;
                    } else if (nextBillingDateA) {
                      return -1;
                    } else if (nextBillingDateB) {
                      return 1;
                    } else {
                      return 0;
                    }
                  }
                }

                let dateA, dateB;

                if (isLastMonth) {
                  dateA = parseLocalDate(
                    a.LastMonthMIOrder.Receive_Final_Payment__c
                  );
                  dateB = parseLocalDate(
                    b.LastMonthMIOrder.Receive_Final_Payment__c
                  );
                } else {
                  dateA = parseLocalDate(
                    a.ThisMonthMIOrder.Receive_Final_Payment__c
                  );
                  dateB = parseLocalDate(
                    b.ThisMonthMIOrder.Receive_Final_Payment__c
                  );
                }

                const isAPaid =
                  (isLastMonth
                    ? a.LastMonthMIOrder.Receive_Final_Payment__c
                    : a.ThisMonthMIOrder.Receive_Final_Payment__c) ||
                  a.ConvertedAmount === 0;
                const isBPaid =
                  (isLastMonth
                    ? b.LastMonthMIOrder.Receive_Final_Payment__c
                    : b.ThisMonthMIOrder.Receive_Final_Payment__c) ||
                  b.ConvertedAmount === 0;

                if (!isAPaid && isBPaid) return -1;
                if (isAPaid && !isBPaid) return 1;
                if (!isAPaid && !isBPaid) return 0;

                if (dateA && dateB) {
                  return dateB - dateA;
                } else if (dateA) {
                  return -1;
                } else if (dateB) {
                  return 1;
                } else {
                  return 0;
                }
              })
              .map((row) => {
                const nextBillingDate = parseLocalDate(
                  row.GMOrder.Next_Billing_Date__c
                );

                // For current month: check if scheduled (future billing date) or overdue (past/no billing date)
                const isScheduled =
                  !isLastMonth &&
                  row.ThisMonthMIOrder.Id &&
                  !row.ThisMonthMIOrder.Receive_Final_Payment__c &&
                  nextBillingDate &&
                  nextBillingDate > new Date() &&
                  row.ConvertedAmount !== 0;

                const isCurrentMonthOverdue =
                  !isLastMonth &&
                  row.ThisMonthMIOrder.Id && // MI order exists for current month
                  !row.ThisMonthMIOrder.Receive_Final_Payment__c && // Payment not received
                  row.ConvertedAmount !== 0 &&
                  (!nextBillingDate || nextBillingDate <= new Date()); // Past due or no future date

                const isOverdue =
                  isLastMonth &&
                  row.LastMonthMIOrder.Id && // MI order exists for last month
                  !row.LastMonthMIOrder.Receive_Final_Payment__c && // Payment not received
                  row.ConvertedAmount !== 0; // Non-zero amount

                const isPaid =
                  (isLastMonth
                    ? row.LastMonthMIOrder.Receive_Final_Payment__c
                    : row.ThisMonthMIOrder.Receive_Final_Payment__c) ||
                  row.ConvertedAmount === 0;

                return (
                  <tr
                    key={row.GMOrder.Id}
                    style={{
                      backgroundColor: row.IsCorporateBillingChild
                        ? "#f8f4ff" // Light purple for corporate billing
                        : isScheduled
                        ? "#ffffff"
                        : isOverdue || isCurrentMonthOverdue
                        ? "#f9e6e6"
                        : isPaid
                        ? "#e6f9e6"
                        : "#f9e6e6",
                    }}
                  >
                    <td
                      style={{
                        padding: "4px",
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={row.GMOrder.Account.Name}
                    >
                      {row.GMOrder.Account.Name ? (
                        <a
                          href={`https://gargleinc.lightning.force.com/${row.GMOrder.AccountId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {truncateText(row.GMOrder.Account.Name, 60)}
                        </a>
                      ) : (
                        ""
                      )}
                      {row.IsCorporateBillingChild && (
                        <span
                          style={{
                            fontSize: "0.7em",
                            color: "#6f42c1",
                            marginLeft: "5px",
                            padding: "2px 4px",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "3px",
                            border: "1px solid #6f42c1",
                          }}
                        >
                          Corp. Billing
                        </span>
                      )}
                      {isScheduled && (
                        <span
                          style={{
                            fontSize: "0.8em",
                            color: "#007bff",
                            marginLeft: "5px",
                          }}
                        >
                          Scheduled on{" "}
                          {nextBillingDate
                            ? nextBillingDate.toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : ""}
                        </span>
                      )}
                      {(isOverdue || isCurrentMonthOverdue) && (
                        <span
                          style={{
                            fontSize: "0.8em",
                            color: "#ff4d4d",
                            marginLeft: "5px",
                          }}
                        >
                          Overdue
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "4px",
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={
                        (
                          isLastMonth
                            ? row.LastMonthMIOrder.Receive_Final_Payment__c
                            : row.ThisMonthMIOrder.Receive_Final_Payment__c
                        )
                          ? (() => {
                              const date = parseLocalDate(
                                isLastMonth
                                  ? row.LastMonthMIOrder
                                      .Receive_Final_Payment__c
                                  : row.ThisMonthMIOrder
                                      .Receive_Final_Payment__c
                              );
                              return date ? date.toLocaleDateString() : "";
                            })()
                          : ""
                      }
                    >
                      {isLastMonth
                        ? row.LastMonthMIOrder.Receive_Final_Payment__c
                          ? (() => {
                              const date = parseLocalDate(
                                row.LastMonthMIOrder.Receive_Final_Payment__c
                              );
                              return date ? date.toLocaleDateString() : "";
                            })()
                          : ""
                        : row.ThisMonthMIOrder.Receive_Final_Payment__c
                        ? (() => {
                            const date = parseLocalDate(
                              row.ThisMonthMIOrder.Receive_Final_Payment__c
                            );
                            return date ? date.toLocaleDateString() : "";
                          })()
                        : ""}
                    </td>
                    <td
                      style={{
                        padding: "4px",
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={
                        row.ConvertedAmount
                          ? row.CurrencyCode !== "USD"
                            ? `${formatCurrency(
                                row.ConvertedAmount
                              )} (${formatCurrency(
                                row.GMOrder.Total_Amount__c,
                                row.CurrencyCode
                              )})`
                            : formatCurrency(row.ConvertedAmount)
                          : formatCurrency(0)
                      }
                    >
                      {row.ConvertedAmount !== 0
                        ? row.CurrencyCode !== "USD"
                          ? `${formatCurrency(
                              row.ConvertedAmount
                            )} (${formatCurrency(
                              row.GMOrder.Total_Amount__c,
                              row.CurrencyCode
                            )})`
                          : formatCurrency(row.ConvertedAmount)
                        : formatCurrency(0)}
                    </td>
                    <td
                      style={{
                        padding: "4px",
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={getNotes(row)}
                    >
                      {getNotes(row)}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    );
  };

  if (!context?.ready) return <Loader />;
  if (loading) return <Loader />;

  // Determine Last and Current Month Labels
  const currentDt = new Date();
  const currentMonthLabel = getMonth(currentDt);
  currentDt.setMonth(currentDt.getMonth() - 1);
  const lastMonthLabel = getMonth(currentDt);

  // Safeguard: Ensure data exists for both months
  const lastMonthCancellations = cancellations[lastMonthLabel] || {
    Cancellations: [],
  };
  const currentMonthCancellations = cancellations[currentMonthLabel] || {
    Cancellations: [],
  };

  const lastMonthRefunds = refunds[lastMonthLabel] || { Transactions: [] };
  const currentMonthRefunds = refunds[currentMonthLabel] || {
    Transactions: [],
  };

  const lastMonthCredits = credits[lastMonthLabel] || { Transactions: [] };
  const currentMonthCredits = credits[currentMonthLabel] || {
    Transactions: [],
  };

  return (
    <>
      {!error && (
        <>
          {!sidebar && <Sidebar />}
          <div className={owId ? "contentWOS dashboard" : "content dashboard"}>
            <div style={{ height: "100%", minHeight: "100%" }}>
              <div className="chart-container">
                <h1>💰 Cash Flow Management Dashboard</h1>

                {/* Executive Summary Cards */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: "20px",
                    marginBottom: "30px",
                  }}
                >
                  {/* Collection Health */}
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      padding: "25px",
                      borderRadius: "15px",
                      textAlign: "center",
                      boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>
                      💰
                    </div>
                    <h3
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "1.1rem",
                        color: "white",
                      }}
                    >
                      Collection Efficiency
                    </h3>
                    <div
                      style={{
                        fontSize: "2rem",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      {isCurrentHigher ? "📈" : "📉"}{" "}
                      {Math.abs(
                        ((currentMonthActualCollection -
                          lastMonthActualCollection) /
                          (lastMonthActualCollection || 1)) *
                          100
                      ).toFixed(1)}
                      %
                    </div>
                    <p
                      style={{
                        margin: 0,
                        opacity: 0.9,
                        fontSize: "0.9rem",
                        color: "white",
                      }}
                    >
                      {isCurrentHigher ? "Improving" : "Declining"} vs last
                      month
                    </p>
                  </div>

                  {/* Current Month Status */}
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #00b894 0%, #00a085 100%)",
                      color: "white",
                      padding: "25px",
                      borderRadius: "15px",
                      textAlign: "center",
                      boxShadow: "0 8px 32px rgba(0, 184, 148, 0.3)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>
                      📊
                    </div>
                    <h3
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "1.1rem",
                        color: "white",
                      }}
                    >
                      This Month
                    </h3>
                    <div
                      style={{
                        fontSize: "1.8rem",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      {formatCurrency(currentMonthActualCollection)}
                    </div>
                    <p
                      style={{
                        margin: 0,
                        opacity: 0.9,
                        fontSize: "0.9rem",
                        color: "white",
                      }}
                    >
                      Net Collection
                    </p>
                  </div>

                  {/* Risk Indicator */}
                  <div
                    style={{
                      background: (() => {
                        const currentMonthOverdue = data.filter(
                          (item) =>
                            item.ThisMonthMIOrder.Id && // MI order exists for current month
                            !item.ThisMonthMIOrder.Receive_Final_Payment__c &&
                            item.ConvertedAmount > 0 &&
                            (!item.GMOrder.Next_Billing_Date__c ||
                              parseLocalDate(
                                item.GMOrder.Next_Billing_Date__c
                              ) <= new Date())
                        ).length;
                        const lastMonthOverdue = data.filter(
                          (item) =>
                            item.LastMonthMIOrder.Id && // MI order exists for last month
                            !item.LastMonthMIOrder.Receive_Final_Payment__c &&
                            item.ConvertedAmount > 0
                        ).length;
                        const totalOverdue =
                          currentMonthOverdue + lastMonthOverdue;
                        return totalOverdue > 5
                          ? "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)"
                          : "linear-gradient(135deg, #4ade80 0%, #16a34a 100%)";
                      })(),
                      color: "white",
                      padding: "25px",
                      borderRadius: "15px",
                      textAlign: "center",
                      boxShadow: (() => {
                        const currentMonthOverdue = data.filter(
                          (item) =>
                            item.ThisMonthMIOrder.Id && // MI order exists for current month
                            !item.ThisMonthMIOrder.Receive_Final_Payment__c &&
                            item.ConvertedAmount > 0 &&
                            (!item.GMOrder.Next_Billing_Date__c ||
                              parseLocalDate(
                                item.GMOrder.Next_Billing_Date__c
                              ) <= new Date())
                        ).length;
                        const lastMonthOverdue = data.filter(
                          (item) =>
                            item.LastMonthMIOrder.Id && // MI order exists for last month
                            !item.LastMonthMIOrder.Receive_Final_Payment__c &&
                            item.ConvertedAmount > 0
                        ).length;
                        const totalOverdue =
                          currentMonthOverdue + lastMonthOverdue;
                        return totalOverdue > 5
                          ? "0 8px 32px rgba(255, 107, 107, 0.3)"
                          : "0 8px 32px rgba(74, 222, 128, 0.3)";
                      })(),
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>
                      {(() => {
                        const currentMonthOverdue = data.filter(
                          (item) =>
                            item.ThisMonthMIOrder.Id && // MI order exists for current month
                            !item.ThisMonthMIOrder.Receive_Final_Payment__c &&
                            item.GMOrder.Total_Amount__c > 0 &&
                            (!item.GMOrder.Next_Billing_Date__c ||
                              parseLocalDate(
                                item.GMOrder.Next_Billing_Date__c
                              ) <= new Date())
                        ).length;
                        const lastMonthOverdue = data.filter(
                          (item) =>
                            item.LastMonthMIOrder.Id && // MI order exists for last month
                            !item.LastMonthMIOrder.Receive_Final_Payment__c &&
                            item.GMOrder.Total_Amount__c > 0
                        ).length;
                        const totalOverdue =
                          currentMonthOverdue + lastMonthOverdue;
                        return totalOverdue > 5 ? "⚠️" : "✅";
                      })()}
                    </div>
                    <h3
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "1.1rem",
                        color: "white",
                      }}
                    >
                      Risk Status
                    </h3>
                    <div
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      {(() => {
                        const currentMonthOverdue = data.filter(
                          (item) =>
                            item.ThisMonthMIOrder.Id && // MI order exists for current month
                            !item.ThisMonthMIOrder.Receive_Final_Payment__c &&
                            item.GMOrder.Total_Amount__c > 0 &&
                            (!item.GMOrder.Next_Billing_Date__c ||
                              parseLocalDate(
                                item.GMOrder.Next_Billing_Date__c
                              ) <= new Date())
                        ).length;
                        const lastMonthOverdue = data.filter(
                          (item) =>
                            item.LastMonthMIOrder.Id && // MI order exists for last month
                            !item.LastMonthMIOrder.Receive_Final_Payment__c &&
                            item.GMOrder.Total_Amount__c > 0
                        ).length;
                        return currentMonthOverdue + lastMonthOverdue;
                      })()}{" "}
                      Overdue
                    </div>
                    <p
                      style={{
                        margin: 0,
                        opacity: 0.9,
                        fontSize: "0.9rem",
                        color: "white",
                      }}
                    >
                      {(() => {
                        const currentMonthOverdue = data.filter(
                          (item) =>
                            item.ThisMonthMIOrder.Id && // MI order exists for current month
                            !item.ThisMonthMIOrder.Receive_Final_Payment__c &&
                            item.GMOrder.Total_Amount__c > 0 &&
                            (!item.GMOrder.Next_Billing_Date__c ||
                              parseLocalDate(
                                item.GMOrder.Next_Billing_Date__c
                              ) <= new Date())
                        ).length;
                        const lastMonthOverdue = data.filter(
                          (item) =>
                            item.LastMonthMIOrder.Id && // MI order exists for last month
                            !item.LastMonthMIOrder.Receive_Final_Payment__c &&
                            item.GMOrder.Total_Amount__c > 0
                        ).length;
                        const totalOverdue =
                          currentMonthOverdue + lastMonthOverdue;
                        return totalOverdue > 5
                          ? "Needs Attention"
                          : "All Good";
                      })()}
                    </p>
                  </div>

                  {/* Action Items */}
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)",
                      color: "white",
                      padding: "25px",
                      borderRadius: "15px",
                      textAlign: "center",
                      boxShadow: "0 8px 32px rgba(253, 203, 110, 0.3)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>
                      🎯
                    </div>
                    <h3
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "1.1rem",
                        color: "white",
                      }}
                    >
                      Action Items
                    </h3>
                    <div
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      {
                        data.filter(
                          (item) =>
                            item.ThisMonthMIOrder.Id &&
                            !item.ThisMonthMIOrder.Receive_Final_Payment__c &&
                            item.GMOrder.Next_Billing_Date__c &&
                            parseLocalDate(item.GMOrder.Next_Billing_Date__c) >
                              new Date() &&
                            item.GMOrder.Total_Amount__c > 0
                        ).length
                      }{" "}
                      Pending
                    </div>
                    <p
                      style={{
                        margin: 0,
                        opacity: 0.9,
                        fontSize: "0.9rem",
                        color: "white",
                      }}
                    >
                      Scheduled Payments
                    </p>
                  </div>
                </div>

                {/* Priority Alerts */}
                {(() => {
                  const currentMonthHighValueOverdue = data.filter(
                    (item) =>
                      item.ThisMonthMIOrder.Id && // MI order exists for current month
                      !item.ThisMonthMIOrder.Receive_Final_Payment__c &&
                      item.GMOrder.Total_Amount__c > 1000 &&
                      (!item.GMOrder.Next_Billing_Date__c ||
                        parseLocalDate(item.GMOrder.Next_Billing_Date__c) <=
                          new Date())
                  );
                  const lastMonthHighValueOverdue = data.filter(
                    (item) =>
                      item.LastMonthMIOrder.Id && // MI order exists for last month
                      !item.LastMonthMIOrder.Receive_Final_Payment__c &&
                      item.GMOrder.Total_Amount__c > 1000
                  );
                  const allHighValueOverdue = [
                    ...currentMonthHighValueOverdue,
                    ...lastMonthHighValueOverdue,
                  ];

                  return (
                    allHighValueOverdue.length > 0 && (
                      <div
                        style={{
                          background:
                            "linear-gradient(to right, #fee2e2, #fecaca)",
                          border: "2px solid #f87171",
                          borderRadius: "15px",
                          padding: "20px",
                          marginBottom: "30px",
                          boxShadow: "0 8px 25px rgba(248, 113, 113, 0.3)",
                        }}
                      >
                        <h3
                          style={{
                            color: "#dc2626",
                            margin: "0 0 15px 0",
                            fontSize: "1.3rem",
                          }}
                        >
                          🚨 Priority Alert: High-Value Overdue Accounts
                        </h3>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "20px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: "1.8rem",
                                fontWeight: "bold",
                                color: "#dc2626",
                              }}
                            >
                              {formatCurrency(
                                allHighValueOverdue.reduce(
                                  (sum, item) =>
                                    sum + item.GMOrder.Total_Amount__c,
                                  0
                                )
                              )}
                            </div>
                            <p style={{ margin: 0, color: "#7f1d1d" }}>
                              {allHighValueOverdue.length} accounts over $1,000
                              need immediate follow-up
                            </p>
                          </div>
                          <button
                            style={{
                              padding: "12px 24px",
                              border: "none",
                              borderRadius: "10px",
                              background: "#dc2626",
                              color: "white",
                              cursor: "pointer",
                              fontSize: "14px",
                              fontWeight: "bold",
                              boxShadow: "0 4px 15px rgba(220, 38, 38, 0.3)",
                            }}
                          >
                            📞 Take Action
                          </button>
                        </div>
                      </div>
                    )
                  );
                })()}

                <input
                  type="text"
                  placeholder="Search by account name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: "8px",
                    marginBottom: "20px",
                    width: "100%",
                    boxSizing: "border-box",
                    fontSize: "14px",
                  }}
                />

                {/* Management Action Dashboard */}
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "20px",
                    padding: "30px",
                    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
                    marginBottom: "30px",
                  }}
                >
                  <h2
                    style={{
                      fontSize: "1.8rem",
                      fontWeight: "bold",
                      marginBottom: "25px",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    📋 Management Action Items
                  </h2>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "20px",
                    }}
                  >
                    {/* Immediate Actions */}
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
                        color: "white",
                        padding: "20px",
                        borderRadius: "15px",
                        boxShadow: "0 8px 25px rgba(255, 107, 107, 0.3)",
                      }}
                    >
                      <h4 style={{ margin: "0 0 15px 0", color: "white" }}>
                        🔴 Immediate Action Required
                      </h4>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "20px",
                          color: "white",
                        }}
                      >
                        <li>
                          Follow up on{" "}
                          {(() => {
                            const currentMonthOverdue = data.filter(
                              (item) =>
                                item.ThisMonthMIOrder.Id && // MI order exists for current month
                                !item.ThisMonthMIOrder
                                  .Receive_Final_Payment__c &&
                                item.GMOrder.Total_Amount__c > 0 &&
                                (!item.GMOrder.Next_Billing_Date__c ||
                                  parseLocalDate(
                                    item.GMOrder.Next_Billing_Date__c
                                  ) <= new Date())
                            ).length;
                            const lastMonthOverdue = data.filter(
                              (item) =>
                                item.LastMonthMIOrder.Id && // MI order exists for last month
                                !item.LastMonthMIOrder
                                  .Receive_Final_Payment__c &&
                                item.GMOrder.Total_Amount__c > 0
                            ).length;
                            return currentMonthOverdue + lastMonthOverdue;
                          })()}{" "}
                          overdue accounts
                        </li>
                        <li>Review payment processing issues</li>
                        <li>Contact high-value delinquent accounts</li>
                      </ul>
                    </div>

                    {/* This Week */}
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)",
                        color: "white",
                        padding: "20px",
                        borderRadius: "15px",
                        boxShadow: "0 8px 25px rgba(253, 203, 110, 0.3)",
                      }}
                    >
                      <h4 style={{ margin: "0 0 15px 0", color: "white" }}>
                        🟡 This Week
                      </h4>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "20px",
                          color: "white",
                        }}
                      >
                        <li>
                          Monitor{" "}
                          {
                            data.filter(
                              (item) =>
                                item.ThisMonthMIOrder.Id &&
                                !item.ThisMonthMIOrder
                                  .Receive_Final_Payment__c &&
                                item.GMOrder.Next_Billing_Date__c &&
                                parseLocalDate(
                                  item.GMOrder.Next_Billing_Date__c
                                ) > new Date() &&
                                parseLocalDate(
                                  item.GMOrder.Next_Billing_Date__c
                                ) <=
                                  new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                            ).length
                          }{" "}
                          payments due this week
                        </li>
                        <li>Review collection process efficiency</li>
                        <li>Update payment reminders</li>
                      </ul>
                    </div>

                    {/* Strategic */}
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #00b894 0%, #00a085 100%)",
                        color: "white",
                        padding: "20px",
                        borderRadius: "15px",
                        boxShadow: "0 8px 25px rgba(0, 184, 148, 0.3)",
                      }}
                    >
                      <h4 style={{ margin: "0 0 15px 0", color: "white" }}>
                        🟢 Strategic Initiatives
                      </h4>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "20px",
                          color: "white",
                        }}
                      >
                        <li>Implement automated payment reminders</li>
                        <li>Review payment terms for new clients</li>
                        <li>Analyze cancellation trends</li>
                      </ul>
                    </div>
                  </div>

                  {/* Key Metrics Row */}
                  <div
                    style={{
                      marginTop: "25px",
                      padding: "20px",
                      background: "rgba(102, 126, 234, 0.1)",
                      borderRadius: "15px",
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: "20px",
                      textAlign: "center",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "1.8rem",
                          fontWeight: "bold",
                          color: "#667eea",
                        }}
                      >
                        {(
                          (data.filter(
                            (item) =>
                              item.ThisMonthMIOrder.Receive_Final_Payment__c
                          ).length /
                            (data.length || 1)) *
                          100
                        ).toFixed(1)}
                        %
                      </div>
                      <div style={{ fontSize: "0.9rem", color: "#666" }}>
                        Collection Rate
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "1.8rem",
                          fontWeight: "bold",
                          color: "#667eea",
                        }}
                      >
                        {formatCurrency(
                          data.reduce(
                            (sum, item) =>
                              sum + (item.GMOrder.Total_Amount__c || 0),
                            0
                          ) / (data.length || 1)
                        )}
                      </div>
                      <div style={{ fontSize: "0.9rem", color: "#666" }}>
                        Avg Account Value
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "1.8rem",
                          fontWeight: "bold",
                          color: "#667eea",
                        }}
                      >
                        {Math.round(
                          (new Date() -
                            new Date(
                              data[0]?.GMOrder?.Account?.Activated_Date__c ||
                                new Date()
                            )) /
                            (1000 * 60 * 60 * 24)
                        ) || 0}
                      </div>
                      <div style={{ fontSize: "0.9rem", color: "#666" }}>
                        Avg Days Outstanding
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "1.8rem",
                          fontWeight: "bold",
                          color: isCurrentHigher ? "#00b894" : "#ff6b6b",
                        }}
                      >
                        {isCurrentHigher ? "📈" : "📉"}{" "}
                        {Math.abs(
                          ((currentMonthActualCollection -
                            lastMonthActualCollection) /
                            (lastMonthActualCollection || 1)) *
                            100
                        ).toFixed(0)}
                        %
                      </div>
                      <div style={{ fontSize: "0.9rem", color: "#666" }}>
                        Month-over-Month
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  {/* Last Month Section */}
                  <div style={{ width: "48%" }}>
                    <h2>{lastMonthLabel}</h2>
                    <hr />
                    {renderTable(
                      data,
                      true,
                      lastMonthRefunds,
                      lastMonthCredits,
                      lastMonthCancellations,
                      undefined // No color override for last month
                    )}
                  </div>

                  {/* Current Month Section */}
                  <div style={{ width: "48%" }}>
                    <h2>{currentMonthLabel}</h2>
                    <hr />
                    {renderTable(
                      data,
                      false,
                      currentMonthRefunds,
                      currentMonthCredits,
                      currentMonthCancellations,
                      actualCollectionColor // Color based on comparison
                    )}
                  </div>
                </div>

                {/* Display Actual Collections Comparison */}
                <div
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <h3>Actual Collections Comparison: </h3>
                  <span
                    style={{
                      marginLeft: "10px",
                      color: actualCollectionColor,
                      fontSize: "1.5em",
                    }}
                  >
                    {actualCollectionIndicator}
                  </span>
                  <span
                    style={{ marginLeft: "5px", color: actualCollectionColor }}
                  >
                    {isCurrentHigher
                      ? "This month is higher"
                      : "This month is lower or equal"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <style jsx>{`
        .content {
          padding: 20px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 10px 0;
        }
        th,
        td {
          border: 1px solid #ddd;
          text-align: left;
          padding: 4px;
          font-size: 11px;
        }
        th {
          background-color: #f4f4f4;
          font-weight: bold;
        }
        .negative {
          color: #dc3545; /* Bootstrap's red color for negative values */
        }
        h5 {
          border-bottom: 1px dotted gray;
        }
        h5.negative {
          text-align: right;
          color: red !important;
        }
        h4 {
          text-align: right;
        }

        @media (max-width: 768px) {
          .last-month-data {
            display: none;
          }

          .notes-column {
            width: 20%;
          }

          .opportunity-name-column {
            flex: 1;
          }
        }

        @media (max-width: 768px) {
          .card-container {
            flex-direction: column;
            align-items: center;
          }

          .card {
            width: 90%;
          }

          table {
            font-size: 10px;
          }

          th,
          td {
            padding: 3px;
          }
        }

        @media (max-width: 480px) {
          .card {
            padding: 10px;
          }

          th,
          td {
            font-size: 9px;
            padding: 2px;
          }

          h1 {
            font-size: 20px;
          }

          h2 {
            font-size: 16px;
          }

          .content {
            padding: 10px;
          }
        }
      `}</style>
    </>
  );
};

export default CashFlowDashboard;
